import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CallContext from '../../context/callContext';
import useAgentCallbacks from '../../hooks/useAgentCallbacks';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Offen', color: 'error' },
  { value: 'inProgress', label: 'In Bearbeitung', color: 'warning' },
  { value: 'done', label: 'Erledigt', color: 'success' },
  { value: 'cancelled', label: 'Abgebrochen', color: 'default' },
  { value: '', label: 'Alle', color: 'default' },
];

const getStatusOption = status =>
  STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0];

export default function AgentCallbacksView() {
  const authCtx = useContext(AuthContext);
  const callCtx = useContext(CallContext);
  const { isLoading, getCallbacks, getCallbackCounts, updateCallback } = useAgentCallbacks();
  const [callbacks, setCallbacks] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('open');
  const [query, setQuery] = useState('');
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const [selectedCallback, setSelectedCallback] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const searchRef = useRef(null);
  const noteRef = useRef(null);

  const canUpdate = authCtx.checkRightAny('agentCallback:update');
  const canCall = authCtx.checkRightAny('call:runcall');

  const selectedAgentSkills = useMemo(() => {
    if (!callCtx.selectedAgent) return [];
    if (Array.isArray(callCtx.selectedAgent.skills) && callCtx.selectedAgent.skills.length) {
      return callCtx.selectedAgent.skills;
    }
    return (callCtx.selectedAgent.skillGroups || []).map(skillId => ({
      id: String(skillId),
      name: `Skill ${skillId}`,
    }));
  }, [callCtx.selectedAgent]);

  useEffect(() => {
    if (!selectedAgentSkills.length) {
      setSelectedSkillId('');
      return;
    }
    if (!selectedAgentSkills.some(skill => String(skill.id) === String(selectedSkillId))) {
      setSelectedSkillId(String(selectedAgentSkills[0].id));
    }
  }, [selectedAgentSkills, selectedSkillId]);

  const loadCallbacks = useCallback(async () => {
    try {
      const [listResponse, countResponse] = await Promise.all([
        getCallbacks({ status: statusFilter, query }),
        getCallbackCounts(),
      ]);
      setCallbacks(Array.isArray(listResponse?.results) ? listResponse.results : []);
      setCounts(countResponse || {});
    } catch (error) {
      toast.error('KI-Rückrufe konnten nicht geladen werden.');
    }
  }, [getCallbackCounts, getCallbacks, query, statusFilter]);

  useEffect(() => {
    loadCallbacks();
    const interval = window.setInterval(loadCallbacks, 15000);
    return () => window.clearInterval(interval);
  }, [loadCallbacks]);

  const refreshNavigationCount = () => {
    authCtx.refreshCountForMenuItem('KI-Rückrufe');
  };

  const changeStatus = async (callbackId, status) => {
    if (!canUpdate) return;
    setActionPending(true);
    try {
      await updateCallback(callbackId, { status });
      toast.success('Rückrufstatus aktualisiert.');
      await loadCallbacks();
      refreshNavigationCount();
    } catch (error) {
      toast.error('Rückrufstatus konnte nicht aktualisiert werden.');
    } finally {
      setActionPending(false);
    }
  };

  const startCall = async callback => {
    if (!canCall || !canUpdate || !callCtx.selectedAgent || !selectedSkillId) return;
    setActionPending(true);
    try {
      const result = await callCtx.initiateDial({
        agentId: callCtx.selectedAgent.id,
        destination: callback.phone,
        skillId: selectedSkillId,
      });
      if (!result?.success) {
        throw new Error('Dial request failed');
      }
      await updateCallback(callback._id, { status: 'inProgress' });
      toast.success('Rückruf über Comdesk gestartet.');
      await loadCallbacks();
      refreshNavigationCount();
    } catch (error) {
      toast.error('Rückruf konnte nicht gestartet werden.');
    } finally {
      setActionPending(false);
    }
  };

  const saveNote = async () => {
    if (!selectedCallback || !canUpdate) return;
    setActionPending(true);
    try {
      await updateCallback(selectedCallback._id, {
        note: noteRef.current?.value || '',
      });
      toast.success('Notiz gespeichert.');
      setSelectedCallback(null);
      await loadCallbacks();
    } catch (error) {
      toast.error('Notiz konnte nicht gespeichert werden.');
    } finally {
      setActionPending(false);
    }
  };

  const handleSearch = () => {
    setQuery(searchRef.current?.value?.trim() || '');
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h4' component='h1' gutterBottom>
            KI-Rückrufe
          </Typography>
          <Typography color='text.secondary'>
            Rückrufwünsche, die der KI-Agent während eines Gesprächs aufgenommen hat.
          </Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id='callback-agent-label'>Comdesk Agent</InputLabel>
              <Select
                labelId='callback-agent-label'
                value={callCtx.selectedAgent?.id || ''}
                label='Comdesk Agent'
                onChange={event => {
                  const agent = callCtx.availableAgents.find(item => item.id === event.target.value);
                  callCtx.setSelectedAgent(agent || null);
                }}
              >
                {callCtx.availableAgents.map(agent => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.description || agent.entertainmentName || agent.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!callCtx.selectedAgent}>
              <InputLabel id='callback-skill-label'>Ausgehender Skill</InputLabel>
              <Select
                labelId='callback-skill-label'
                value={selectedSkillId}
                label='Ausgehender Skill'
                onChange={event => setSelectedSkillId(event.target.value)}
              >
                {selectedAgentSkills.map(skill => (
                  <MenuItem key={skill.id} value={String(skill.id)}>
                    {skill.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {(!canCall || !canUpdate) && (
            <Alert severity='info' sx={{ mt: 2 }}>
              Zum Starten eines Rückrufs werden zusätzlich die Rechte für Anrufausführung und
              Bearbeitung von KI-Rückrufen benötigt.
            </Alert>
          )}
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ flex: 1 }}>
            {STATUS_OPTIONS.map(option => (
              <Chip
                key={option.value || 'all'}
                label={`${option.label}${
                  option.value && typeof counts[option.value] === 'number'
                    ? ` (${counts[option.value]})`
                    : ''
                }`}
                color={statusFilter === option.value ? option.color : 'default'}
                variant={statusFilter === option.value ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(option.value)}
              />
            ))}
          </Stack>
          <Stack direction='row' spacing={1}>
            <TextField
              inputRef={searchRef}
              size='small'
              placeholder='Name, Telefon, Buchungsnummer'
              defaultValue={query}
              onKeyDown={event => {
                if (event.key === 'Enter') handleSearch();
              }}
            />
            <Button variant='outlined' onClick={handleSearch}>
              Suchen
            </Button>
            <Button variant='outlined' onClick={loadCallbacks} startIcon={<RefreshIcon />}>
              Aktualisieren
            </Button>
          </Stack>
        </Stack>

        {isLoading && !callbacks.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Eingang</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Anliegen</TableCell>
                  <TableCell>Kontext</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {callbacks.map(callback => {
                  const statusOption = getStatusOption(callback.status);
                  return (
                    <TableRow key={callback._id} hover>
                      <TableCell>
                        {dayjs(callback.meta?.createdAt).format('DD.MM.YYYY HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{callback.name || 'Name unbekannt'}</Typography>
                        <Typography variant='body2'>{callback.phone}</Typography>
                        {callback.preferredTime && (
                          <Typography variant='caption' color='text.secondary'>
                            Gewünscht: {callback.preferredTime}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{callback.reason}</Typography>
                        {callback.note && (
                          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                            Notiz: {callback.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>
                          Standort: {callback.location || 'nicht angegeben'}
                        </Typography>
                        <Typography variant='body2'>
                          Buchung: {callback.bookingNumber || 'nicht angegeben'}
                        </Typography>
                        {callback.callId && (
                          <Typography variant='caption' color='text.secondary'>
                            Gesprächs-ID: {callback.callId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          color={statusOption.color}
                          label={statusOption.label}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Stack direction='row' spacing={1} justifyContent='flex-end'>
                          <Button
                            size='small'
                            variant='contained'
                            color='success'
                            startIcon={<CallIcon />}
                            disabled={
                              actionPending ||
                              !canCall ||
                              !canUpdate ||
                              !callCtx.selectedAgent ||
                              !selectedSkillId ||
                              callback.status === 'done' ||
                              callback.status === 'cancelled'
                            }
                            onClick={() => startCall(callback)}
                          >
                            Anrufen
                          </Button>
                          <Button
                            size='small'
                            variant='outlined'
                            onClick={() => setSelectedCallback(callback)}
                          >
                            Details
                          </Button>
                          {callback.status !== 'done' && canUpdate && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='success'
                              disabled={actionPending}
                              onClick={() => changeStatus(callback._id, 'done')}
                            >
                              Erledigt
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!callbacks.length && (
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 8 }}>
                      Keine Rückrufwünsche für diesen Filter vorhanden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Dialog
        open={Boolean(selectedCallback)}
        onClose={() => setSelectedCallback(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Rückruf bearbeiten</DialogTitle>
        <DialogContent>
          {selectedCallback && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography>
                <strong>{selectedCallback.name || 'Name unbekannt'}</strong> ·{' '}
                {selectedCallback.phone}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedCallback.reason}</Typography>
              <TextField
                inputRef={noteRef}
                label='Bearbeitungsnotiz'
                defaultValue={selectedCallback.note || ''}
                multiline
                minRows={4}
                fullWidth
                disabled={!canUpdate}
              />
              {canUpdate && (
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <Button
                    variant='outlined'
                    onClick={() => changeStatus(selectedCallback._id, 'open')}
                  >
                    Wieder öffnen
                  </Button>
                  <Button
                    variant='outlined'
                    color='warning'
                    onClick={() => changeStatus(selectedCallback._id, 'inProgress')}
                  >
                    In Bearbeitung
                  </Button>
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={() => changeStatus(selectedCallback._id, 'cancelled')}
                  >
                    Abbrechen
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCallback(null)}>Schließen</Button>
          {canUpdate && (
            <Button variant='contained' onClick={saveNote} disabled={actionPending}>
              Notiz speichern
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
